import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, CheckCircle, Key, ArrowLeft, Warning, BookOpen } from "@phosphor-icons/react";
import { fetchAccessMe, fetchAccessPermissions } from "@/lib/api";
import { useT } from "@/lib/i18n";
import AllToursModal from "@/components/AllToursModal";
import MyChecklist from "@/components/MyChecklist";
import { TOURS } from "@/lib/tours";

/**
 * Self-view of the signed-in user's rights. Any authenticated user (or even a
 * guest) may open this — there's nothing sensitive to leak, it just shows
 * whatever they can already do.
 */
export default function MyAccessPage() {
  const t = useT();
  const [me, setMe] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [err, setErr] = useState("");
  const [toursOpen, setToursOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [meR, permsR] = await Promise.all([fetchAccessMe(), fetchAccessPermissions()]);
        setMe(meR);
        setCatalog(permsR.catalog || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load");
      }
    })();
  }, []);

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white border border-red-200 rounded-sm p-6 max-w-md" data-testid="my-access-error">
          <Warning size={24} className="text-red-600 mb-2" />
          <p className="text-sm text-red-700">{err}</p>
        </div>
      </div>
    );
  }
  if (!me) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;

  const granted = new Set(me.permissions || []);
  const userRightsGranted = new Set(me.user_rights?.granted || []);
  const userRightsRevoked = new Set(me.user_rights?.revoked || []);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 sticky top-0 z-10" data-testid="my-access-header">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Link to="/us-class" data-testid="my-access-back" className="text-slate-500 hover:text-slate-800">
            <ArrowLeft size={18} />
          </Link>
          <div className="flex items-center gap-2">
            <Key size={16} weight="bold" className="text-blue-700" />
            <h1 className="text-sm font-bold font-['Chivo'] tracking-tight">{t("my_access.title", "My access")}</h1>
          </div>
          <div className="ml-auto text-xs text-slate-500 truncate max-w-[220px]">{me.email || "Guest"}</div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 sm:p-6 space-y-5">
        <MyChecklist />
        {/* Summary */}
        <section className="bg-white border border-slate-200 rounded-sm p-5" data-testid="my-access-summary">
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Role</p>
              <p className="text-lg font-bold text-slate-900 font-['Chivo']" data-testid="my-access-role">{me.role || "— no role —"}</p>
            </div>
            {me.is_super_admin && (
              <span data-testid="my-access-super-admin" className="ml-auto flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-purple-50 text-purple-700 border border-purple-200 px-2 py-1 rounded-sm">
                <ShieldCheck size={11} weight="fill" /> Super Admin
              </span>
            )}
            {!me.is_super_admin && me.is_legacy_admin && (
              <span className="ml-auto flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200 px-2 py-1 rounded-sm">
                <ShieldCheck size={11} weight="fill" /> Admin
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {t("my_access.perm_summary", "You have {count} permission(s) active.", { count: granted.size })}
            {me.user_rights?.updated_by
              ? " " + t("my_access.last_updated", "Last updated by {who} on {date}.", {
                  who: me.user_rights.updated_by,
                  date: (me.user_rights.updated_at || '').slice(0, 10),
                })
              : ""}
          </p>

          {/* iter146 — Reset all tours: clears the four *_tour_seen_v1
              localStorage flags so first-visit banners re-appear. Useful
              for admins demoing the app or replaying their onboarding. */}
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-3 flex-wrap">
            <button
              data-testid="my-access-view-tours"
              onClick={() => setToursOpen(true)}
              className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-sm border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
            >
              <BookOpen size={12} weight="bold" /> View all tours
            </button>
            <span className="text-[11px] text-slate-500">
              Replay or reset the {TOURS.length} interactive page tours (Requested Changes, Evidence Rules, Warranty Chat, Test Queue).
            </span>
          </div>
        </section>

        {/* Per-category permissions */}
        {catalog.map((cat) => {
          const granted_in_cat = cat.items.filter((p) => granted.has(p.key)).length;
          return (
            <section key={cat.category} className="bg-white border border-slate-200 rounded-sm" data-testid={`my-access-cat-${cat.category}`}>
              <header className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-slate-50">
                <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">{cat.category}</h2>
                <span className="text-[10px] font-mono text-slate-500">{granted_in_cat}/{cat.items.length}</span>
              </header>
              <ul className="divide-y divide-slate-100">
                {cat.items.map((p) => {
                  const has = granted.has(p.key);
                  const tag = userRightsGranted.has(p.key) ? "GRANTED"
                    : userRightsRevoked.has(p.key) ? "REVOKED"
                    : has ? "FROM ROLE" : "NOT AVAILABLE";
                  const tagColor = userRightsGranted.has(p.key) ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : userRightsRevoked.has(p.key) ? "bg-red-50 text-red-700 border-red-200"
                    : has ? "bg-blue-50 text-blue-700 border-blue-200"
                    : "bg-slate-100 text-slate-400 border-slate-200";
                  return (
                    <li key={p.key} data-testid={`my-access-perm-${p.key}`} className={`flex items-center gap-3 px-4 py-2 text-sm ${has ? "" : "opacity-60"}`}>
                      {has ? <CheckCircle size={14} weight="fill" className="text-emerald-500" /> : <span className="w-3.5 h-3.5 border border-slate-300 rounded-full" />}
                      <span className="flex-1 min-w-0 truncate">{p.label}</span>
                      <span className={`text-[9px] font-bold border px-1.5 py-0.5 rounded-sm ${tagColor}`}>{tag}</span>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </main>

      <AllToursModal open={toursOpen} onClose={() => setToursOpen(false)} user={me} />
    </div>
  );
}
